/**
 * RunTimer — elapsed time for the current question (⏳ + duration).
 *
 * Anchored to the run's start/end timestamps in the STORE (set on startRun /
 * finalizeRun / markCancelled / hydrateFromDetail), NOT a component-local ref.
 * That way it captures the WHOLE run duration and survives tab-switches and
 * re-mounts — leaving and returning to the page no longer resets it. Live-ticks
 * while the run is in flight, then freezes at the final duration.
 */
import { useEffect, useState } from "react";
import { Hourglass } from "lucide-react";
import { useMultiAgentStore } from "@/stores/multiAgentStore";

function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
}

export function RunTimer() {
  const startedAt = useMultiAgentStore((s) => s.runStartedAt);
  const finishedAt = useMultiAgentStore((s) => s.runFinishedAt);
  const status = useMultiAgentStore((s) => s.status);

  const inFlight =
    status === "starting" || status === "planning" || status === "running" || status === "awaiting_approval";
  // Counting only while the run is genuinely in flight and not yet stamped done.
  const active = startedAt != null && finishedAt == null && inFlight;

  const [, force] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => force((x) => x + 1), 500);
    return () => clearInterval(id);
  }, [active]);

  if (startedAt == null) return null;
  // Elapsed is always (end-or-now) − the persisted start, so it's correct
  // regardless of how many times this component re-mounts/re-renders.
  const elapsed = (finishedAt ?? Date.now()) - startedAt;

  return (
    <span
      className="ma-micro inline-flex shrink-0 items-center gap-1"
      style={{
        textTransform: "none",
        letterSpacing: 0,
        color: active ? "var(--ma-accent)" : "var(--ma-text-muted)",
        fontVariantNumeric: "tabular-nums",
      }}
      title={active ? "Time elapsed for this question" : "Time this question took"}
    >
      <Hourglass className={`h-3 w-3 ${active ? "animate-pulse" : ""}`} />
      {fmtDuration(elapsed)}
    </span>
  );
}
